import type { ColumnDef } from '@tanstack/react-table';
import { Badge } from '@/components/ui/badge';
import { Star } from 'lucide-react';
import type { Venue } from '../../types';
import { VenueActions } from '../VenueActions/VenueActions';

type VenueWithHandlers = Venue & {
  onViewDetails?: (venue: Venue) => void;
};

export const getVenueColumns = (): ColumnDef<VenueWithHandlers>[] => [
  {
    accessorKey: 'id',
    header: 'ID',
    cell: ({ row }) => (
      <div data-testid={`venue-id-${row.original.id}`} className="font-mono text-xs">
        {row.original.id}
      </div>
    ),
    size: 60,
  },
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => (
      <div data-testid={`venue-name-${row.original.id}`}>
        <button
          className="font-medium text-primary hover:underline text-left"
          onClick={() => row.original.onViewDetails?.(row.original)}
        >
          {row.original.name}
        </button>
      </div>
    ),
  },
  {
    accessorKey: 'primary_type',
    header: 'Type',
    cell: ({ row }) => (
      <Badge variant="secondary" data-testid={`venue-type-${row.original.id}`}>
        {row.original.primary_type}
      </Badge>
    ),
  },
  {
    accessorKey: 'formatted_address',
    header: 'Address',
    cell: ({ row }) => (
      <div data-testid={`venue-address-${row.original.id}`} className="max-w-md">
        <p className="truncate text-sm" title={row.original.formatted_address}>
          {row.original.formatted_address}
        </p>
      </div>
    ),
  },
  {
    accessorKey: 'rating',
    header: 'Rating',
    cell: ({ row }) => {
      const rating = row.original.rating;
      const reviewCount = row.original.user_rating_count;
      
      if (!rating || rating === 0) {
        return <span className="text-muted-foreground text-sm">No ratings</span>;
      }
      
      return (
        <div className="flex items-center gap-1" data-testid={`venue-rating-${row.original.id}`}>
          <Star className="h-4 w-4 fill-yellow-500 text-yellow-500" />
          <span className="font-medium text-sm">{rating.toFixed(1)}</span>
          <span className="text-xs text-muted-foreground">({reviewCount})</span>
        </div>
      );
    },
  },
  {
    accessorKey: 'price_range',
    header: 'Price Range',
    cell: ({ row }) => (
      <div data-testid={`venue-price-${row.original.id}`} className="text-sm">
        {row.original.price_range || '-'}
      </div>
    ),
  },
  {
    accessorKey: 'organization.name',
    header: 'Organization',
    cell: ({ row }) => (
      <div data-testid={`venue-organization-${row.original.id}`} className="text-sm">
        {row.original.organization?.name || '-'}
      </div>
    ),
  },
  {
    id: 'actions',
    cell: ({ row }) => <VenueActions venue={row.original} />,
    size: 60,
  },
];